import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUsers } from "@/lib/hooks";
import { fileToDataUrl } from "@/lib/files";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { AccessLogPanel } from "@/components/AccessLogPanel";
import { ASSIGNABLE_ROLES, ROLE_LABELS, hasRole, userRoles } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Copy, KeyRound, Lock, Plus, Trash2, UserCog, Mail, X } from "lucide-react";

/** Avatar que, para quem tem permissão, abre o seletor de imagem ao clicar. */
function EditableAvatar({ user, size, canEdit, onPick }) {
  if (!canEdit) return <UserAvatar user={user} size={size} />;
  return (
    <label className="relative shrink-0 cursor-pointer" title="Alterar fotografia" data-testid={`avatar-edit-${user.id}`}>
      <UserAvatar user={user} size={size} />
      <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-primary text-black">
        <Camera className="h-2.5 w-2.5" />
      </span>
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { onPick(user, e.target.files?.[0]); e.target.value = ""; }}
      />
    </label>
  );
}

export default function UserManagement() {
  const qc = useQueryClient();
  const { user: me, updateUser } = useAuth();
  const { data: users = [] } = useUsers();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", roles: [] });
  const [saving, setSaving] = useState(false);

  const [resetting, setResetting] = useState(null);   // utilizador a repor
  const [newPassword, setNewPassword] = useState(""); // password gerada, mostrada uma vez

  // Gerir contas (editar, desativar, eliminar) continua a ser da gestão; criar
  // é de todos; repor passwords e pôr fotografias é da gestão e de quem faz
  // suporte técnico.
  const isManager = ["ADMIN", "SUB_ADMIN"].includes(me.role);
  const canReset = me.role === "ADMIN" || hasRole(me, "WEB_DEVELOPER");
  const canEditAvatars = isManager || hasRole(me, "WEB_DEVELOPER");

  // Os "Logs" só existem para as contas que o servidor autoriza
  // (ACCESS_LOG_VIEWERS), independentemente das funções, e pedem password.
  const { data: canViewAccess = false } = useQuery({
    queryKey: ["access-logs", "allowed"],
    queryFn: async () => (await api.get("/access-logs/allowed")).data.allowed,
  });
  const [showLogs, setShowLogs] = useState(false);
  const closeLogs = async () => {
    setShowLogs(false);
    try { await api.post("/access-logs/lock"); } catch { /* fecha na mesma */ }
    qc.removeQueries({ queryKey: ["access-logs", "data"] });
  };

  // CEO e Accounting Assistant só são atribuídos pelo CEO.
  const rolesForMe = ASSIGNABLE_ROLES.filter((r) => (r === "ADMIN" || r === "FINANCE" ? me.role === "ADMIN" : true));
  const toggleRole = (r) =>
    setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const openNew = () => { setEditing(null); setForm({ name: "", username: "", email: "", password: "", roles: [] }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, username: u.username || "", email: u.email, password: "", roles: userRoles(u) }); setOpen(true); };

  const save = async () => {
    if (form.roles.length === 0) return toast.error("Escolha pelo menos uma função");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, username: form.username, roles: form.roles });
        toast.success("Utilizador atualizado");
      } else {
        if (!form.name || !form.username || !form.email || !form.password) { setSaving(false); return toast.error("Preencha todos os campos"); }
        await api.post("/users", form);
        toast.success("Utilizador criado");
      }
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { status: u.status === "ATIVO" ? "INATIVO" : "ATIVO" });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {});
      setResetting(u);
      setNewPassword(data.password);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changeAvatar = async (u, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Imagem demasiado grande (máx. 2MB)");
    try {
      const avatarUrl = await fileToDataUrl(file);
      const { data } = await api.put(`/users/${u.id}/avatar`, { avatarUrl });
      if (u.id === me.id) updateUser(data);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Fotografia de ${u.name} atualizada`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (me.role !== "ADMIN") return toast.error("Apenas Admin pode eliminar");
    try { await api.delete(`/users/${u.id}`); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Eliminado"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Gestão de Utilizadores"
        subtitle="Membros da equipa e respetivas permissões."
        actions={
          <div className="flex items-center gap-2">
            {canViewAccess && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (showLogs ? closeLogs() : setShowLogs(true))}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="logs-toggle"
              >
                {showLogs ? <X className="h-3.5 w-3.5 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                {showLogs ? "Fechar logs" : "Logs"}
              </Button>
            )}
            <Button onClick={openNew} className="bg-primary text-black hover:bg-primary/90 rounded-lg" data-testid="new-user-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Novo Utilizador
            </Button>
          </div>
        }
      />

      {canViewAccess && showLogs ? <AccessLogPanel /> : (<>
      {/* Telemóvel: um cartão por pessoa. A tabela só a partir de `md`, onde há
          largura para as quatro colunas sem cortar nomes nem emails. */}
      <div className="space-y-3 md:hidden">
        {users.map((u) => {
          const actions = [
            canReset && u.id !== me.id && (
              <Button key="reset" variant="outline" size="sm" className="rounded-full" onClick={() => resetPassword(u)} data-testid={`reset-user-m-${u.id}`}>
                <KeyRound className="h-4 w-4 mr-1.5" /> Palavra-passe
              </Button>
            ),
            isManager && (
              <Button key="edit" variant="outline" size="sm" className="rounded-full" onClick={() => openEdit(u)} data-testid={`edit-user-m-${u.id}`}>
                <UserCog className="h-4 w-4 mr-1.5" /> Editar
              </Button>
            ),
            me.role === "ADMIN" && u.id !== me.id && (
              <Button key="del" variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => del(u)} data-testid={`delete-user-m-${u.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            ),
          ].filter(Boolean);

          return (
            <Card key={u.id} className="border-border p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start gap-3">
                <EditableAvatar user={u} size="h-11 w-11" canEdit={canEditAvatars} onPick={changeAvatar} />
                <div className="min-w-0 flex-1">
                  <div className="font-poppins font-semibold leading-tight truncate">{u.name}</div>
                  {u.username && <div className="text-xs text-muted-foreground truncate">@{u.username}</div>}
                  <div className="mt-0.5 text-xs text-muted-foreground flex items-center gap-1 min-w-0">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{u.email}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Switch checked={u.status === "ATIVO"} onCheckedChange={() => toggleStatus(u)} disabled={u.id === me.id || !isManager} data-testid={`status-m-${u.id}`} />
                  <span className="text-[10px] text-muted-foreground">{u.status === "ATIVO" ? "Ativo" : "Inativo"}</span>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {userRoles(u).map((r) => (
                  <Badge key={r} variant="secondary" className="rounded-full font-normal">{ROLE_LABELS[r] || r}</Badge>
                ))}
              </div>

              {actions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap gap-2">{actions}</div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="border-border overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-muted/40 border-b border-border">
                <th className="py-3 px-4 font-poppins">Membro</th>
                <th className="py-3 px-4 font-poppins">Função</th>
                <th className="py-3 px-4 font-poppins">Estado</th>
                <th className="py-3 px-4 font-poppins text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/60 hover:bg-muted/20" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <EditableAvatar user={u} canEdit={canEditAvatars} onPick={changeAvatar} />
                      <div>
                        <div className="font-poppins font-medium">{u.name}</div>
                        {u.username && <div className="text-xs text-muted-foreground">@{u.username}</div>}
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {userRoles(u).map((r) => (
                        <Badge key={r} variant="secondary" className="rounded-full">{ROLE_LABELS[r] || r}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={u.status === "ATIVO"} onCheckedChange={() => toggleStatus(u)} disabled={u.id === me.id || !isManager} data-testid={`status-${u.id}`} />
                      <span className="text-xs text-muted-foreground">{u.status === "ATIVO" ? "Ativo" : "Inativo"}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canReset && u.id !== me.id && (
                        <Button variant="ghost" size="sm" onClick={() => resetPassword(u)} title="Repor palavra-passe" data-testid={`reset-user-${u.id}`}>
                          <KeyRound className="h-4 w-4" />
                        </Button>
                      )}
                      {isManager && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><UserCog className="h-4 w-4" /></Button>
                      )}
                      {me.role === "ADMIN" && u.id !== me.id && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      </>)}

      <Dialog open={Boolean(resetting)} onOpenChange={(value) => { if (!value) { setResetting(null); setNewPassword(""); } }}>
        <DialogContent className="max-w-md" data-testid="reset-password-modal">
          <DialogHeader><DialogTitle className="font-poppins">Palavra-passe reposta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Nova palavra-passe de <b>{resetting?.name}</b>. Só aparece esta vez.
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={newPassword} className="font-mono" data-testid="reset-password-value" />
              <Button
                variant="outline"
                size="icon"
                onClick={() => { navigator.clipboard?.writeText(newPassword); toast.success("Copiada"); }}
                aria-label="Copiar palavra-passe"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setResetting(null); setNewPassword(""); }} className="bg-primary text-black hover:bg-primary/90">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-modal">
          <DialogHeader><DialogTitle className="font-poppins">{editing ? "Editar Utilizador" : "Novo Utilizador"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome completo</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="user-name" /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1" data-testid="user-username" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" disabled={!!editing} data-testid="user-email" /></div>
            {!editing && <div><Label>Palavra-passe inicial</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="user-password" /></div>}
            <div><Label>Funções</Label>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="user-roles">
                {rolesForMe.map((r) => {
                  const active = form.roles.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${active ? "bg-primary text-black border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                    >
                      {ROLE_LABELS[r]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="user-save">{saving ? "A guardar..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
